import type { LiftAccessRuleSaveResult } from "../../models";
import { isRetryableSaveError } from "../saveErrorCodes";
import { LiftAccessRuleSet, liftAccessRuleSetKey } from "./models";
import type { BulkSaveFailure } from "./BulkSaveFailuresView";

/**
 * A bulk delete run and everything learned about it across the first attempt and any
 * retries. Keyed by rule set rather than by request: a delete carries no per-rule-set
 * request to key on, only the rule sets themselves.
 */
export type BulkDeleteRun = {
  /** Everything the run set out to delete, so anything it never reached is still known. */
  ruleSets: LiftAccessRuleSet[];
  /**
   * Latest outcome per rule set. Keyed, not appended: a retry only reports on what it
   * re-sent, so counts are recalculated per rule set rather than accumulated.
   */
  outcomes: Map<string, LiftAccessRuleSaveResult>;
  /** The user stopped the run. Nothing failed on its own account. */
  cancelled: boolean;
};

/** Rule sets no attempt has reached — the run stopped or was cancelled before them. */
export const notAttemptedRuleSets = (run: BulkDeleteRun): LiftAccessRuleSet[] =>
  run.ruleSets.filter(
    (ruleSet) => !run.outcomes.has(liftAccessRuleSetKey(ruleSet)),
  );

/** Failed rule sets as the outcome grid shows them. */
export const toDeleteFailures = (run: BulkDeleteRun): BulkSaveFailure[] => {
  const byKey = new Map(run.ruleSets.map((rs) => [liftAccessRuleSetKey(rs), rs]));

  return [...run.outcomes.entries()]
    .filter(([, result]) => !result.success)
    .map(([key, result]): BulkSaveFailure | undefined => {
      const ruleSet = byKey.get(key);
      // Dropped rather than rendered with blank identifying columns.
      if (!ruleSet) return undefined;
      return {
        ruleSet,
        errorStatus: result.errorStatus,
        errorCode: result.errorCode,
        errorMessage: result.errorMessage,
      };
    })
    .filter((failure): failure is BulkSaveFailure => !!failure);
};

/**
 * Rule sets worth sending again: the transient failures only.
 *
 * Deliberately narrower than the save flow's equivalent, which also re-sends anything
 * unattempted. Retry here is a second irreversible bulk delete behind a typed
 * confirmation, and the confirmation states a count — so that count has to mean "the
 * errors that might clear", not "everything outstanding". Rule sets the run never
 * reached stay selected instead, and a BA who wants them can delete them again.
 */
export const retryableDeleteRuleSets = (
  run: BulkDeleteRun,
): LiftAccessRuleSet[] => {
  const retryableKeys = new Set(
    [...run.outcomes.entries()]
      .filter(([, result]) => !result.success && isRetryableSaveError(result))
      .map(([key]) => key),
  );

  return run.ruleSets.filter((ruleSet) =>
    retryableKeys.has(liftAccessRuleSetKey(ruleSet)),
  );
};

export const deleteSuccessCount = (run: BulkDeleteRun): number =>
  [...run.outcomes.values()].filter((outcome) => outcome.success).length;

export const deleteFailureCount = (run: BulkDeleteRun): number =>
  [...run.outcomes.values()].filter((outcome) => !outcome.success).length;
